import {
  AlertRuleChannelRow,
  AlertRuleConditionRow,
  AlertRuleHistoryAction,
  AlertRuleHistoryEntry,
} from '@src/api/alertRules';
import { getTimeZone } from '@src/utils/timezone';
import { isForeverUntil } from '@src/components/common/RuleStatus';

export const ACTION_LABELS: Record<AlertRuleHistoryAction, string> = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  channel_attached: 'Channel added',
  channel_detached: 'Channel removed',
};

export const ACTION_COLORS: Record<AlertRuleHistoryAction, string> = {
  created: 'green',
  updated: 'blue',
  deleted: 'red',
  channel_attached: 'geekblue',
  channel_detached: 'orange',
};

const ruleTypeLabel = (ruleType: string) =>
  ruleType === 'deny' ? 'Mute specific' : 'Mute all except';

export const formatDateTime = (iso: string): string => {
  if (!iso) {
    return '';
  }
  const tz = getTimeZone() || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
};

const formatWindowBoundary = (iso: string): string =>
  isForeverUntil(iso) ? 'No end date' : formatDateTime(iso);

interface LabelResolvers {
  resolveDimensionLabel: (key: string) => string;
  resolveMetricLabel: (key: string) => string;
}

const describeCondition = (c: AlertRuleConditionRow, resolvers: LabelResolvers): string => {
  if (c.conditionType === 'metric') {
    return `metric ${resolvers.resolveMetricLabel(c.metricKey || '')}`;
  }
  const dim = resolvers.resolveDimensionLabel(c.dimensionKey || '');
  if (c.matchMode === 'has_dimension') {
    return `has ${dim}`;
  }
  if (c.matchMode === 'dimension_contains') {
    return `${dim} contains ${c.dimensionLabel || c.dimensionValue}`;
  }
  return `${dim} = ${c.dimensionLabel || c.dimensionValue}`;
};

const describeChannel = (c: AlertRuleChannelRow): string =>
  `${c.channelType}${c.target ? `: ${c.target}` : ''}`;

/**
 * Turn a history entry into human-readable bullet lines. Create/delete/channel
 * events are single-line; updates expand into per-field change descriptions.
 */
export const describeChanges = (
  entry: AlertRuleHistoryEntry,
  resolvers: LabelResolvers,
): string[] => {
  const cf = entry.changedFields || {};

  switch (entry.action) {
    case 'created':
      return ['Rule created'];
    case 'deleted':
      return ['Rule deleted'];
    case 'channel_attached':
      return (cf.channelsAdded || []).map((c) => `Attached channel ${describeChannel(c)}`);
    case 'channel_detached':
      return (cf.channelsRemoved || []).map((c) => `Removed channel ${describeChannel(c)}`);
    case 'updated':
    default: {
      const lines: string[] = [];
      if (cf.name) {
        lines.push(`Name: "${cf.name.from}" \u2192 "${cf.name.to}"`);
      }
      if (cf.ruleType) {
        lines.push(
          `Type: ${ruleTypeLabel(cf.ruleType.from)} \u2192 ${ruleTypeLabel(cf.ruleType.to)}`,
        );
      }
      if (cf.activeFrom) {
        lines.push(
          `Start: ${formatDateTime(cf.activeFrom.from)} \u2192 ${formatDateTime(cf.activeFrom.to)}`,
        );
      }
      if (cf.activeUntil) {
        lines.push(
          `End: ${formatWindowBoundary(cf.activeUntil.from)} \u2192 ${formatWindowBoundary(
            cf.activeUntil.to,
          )}`,
        );
      }
      (cf.conditionsAdded || []).forEach((c) =>
        lines.push(`Added condition: ${describeCondition(c, resolvers)}`),
      );
      (cf.conditionsRemoved || []).forEach((c) =>
        lines.push(`Removed condition: ${describeCondition(c, resolvers)}`),
      );
      (cf.channelsAdded || []).forEach((c) => lines.push(`Added channel: ${describeChannel(c)}`));
      (cf.channelsRemoved || []).forEach((c) =>
        lines.push(`Removed channel: ${describeChannel(c)}`),
      );
      return lines.length ? lines : ['No field changes'];
    }
  }
};
